import ArticleLayout, { P, H2, H3, UL, LI, Strong, Note } from '../../components/ArticleLayout.jsx';

export default function CalorieMath({ related }) {
  return (
    <ArticleLayout
      title="How TrainForge calculates your calories"
      summary="A plain-English walkthrough of how we turn your weight, activity, and goal into a daily calorie target — and why the number adapts every week once you've logged data."
      readingMin={6}
      date="2026-05-22"
      related={related}
    >
      <P>
        Every calorie calculator on the internet asks for your weight, height, age, and activity level, then spits out a number. Most of them are wrong — not because the formula is broken, but because nobody actually burns calories at the rate a formula predicts. Two people with identical stats can have maintenance calories that differ by 400+. TrainForge handles this in two stages: an estimate when you first sign up, then a measured target once we have real data to work with.
      </P>

      <H2>Stage 1: the initial estimate</H2>
      <P>
        When you finish onboarding, we don't have any food logs or weight history yet, so we use a standard activity-multiplier method:
      </P>
      <Note>
        <Strong>Maintenance calories ≈ body weight (lbs) × activity multiplier</Strong>
        <br />
        Sedentary = 13 · Light = 14 · Moderate = 15 · Active = 16 · Very active = 17
      </Note>
      <P>
        A 180 lb member who selected "moderate" activity gets an initial maintenance estimate of 2,700 cal/day (180 × 15). This is rough, but it's good enough to start with — better than waiting two weeks for data before giving you any target at all.
      </P>

      <H2>Stage 2: goal adjustment</H2>
      <P>
        Maintenance is the number that keeps your weight stable. To change body composition, we add or subtract calories based on your goal. The amounts aren't arbitrary — they come from one rule:
      </P>
      <Note>
        <Strong>3,500 kcal ≈ 1 lb of body fat.</Strong> Spread over 7 days, a 500/day deficit creates roughly 1 lb of fat loss per week.
      </Note>
      <P>Here's how each goal translates that into a daily target:</P>
      <UL>
        <LI><Strong>Lose fat:</Strong> −500/day → ~1 lb/week loss. Big enough to see results, small enough to preserve muscle if you train hard and eat enough protein.</LI>
        <LI><Strong>Build muscle:</Strong> +300/day on training days, near maintenance on rest days → ~0.5 lb/week gain. Lean muscle gain is slower than fat loss; bigger surpluses just add more fat.</LI>
        <LI><Strong>Get stronger:</Strong> Near maintenance with a small training-day boost. Focus is on lifting heavier, not changing the scale.</LI>
        <LI><Strong>Recomp:</Strong> +250 on training days, −200 on rest days. Net is a slight weekly deficit while training-day surplus fuels muscle. The hardest goal — see <a href="/learn/recomp-myth" style={{ color: 'var(--accent)' }}>why recomp is hard</a>.</LI>
        <LI><Strong>Maintain:</Strong> Maintenance every day. Useful between cuts and bulks.</LI>
      </UL>
      <P>
        Protein is calculated separately as grams per pound of body weight: 1.2 g/lb for fat loss (high — protects muscle in a deficit), 1.1 g/lb for recomp, 1.0 g/lb for muscle/strength, 0.8 g/lb for general health.
      </P>

      <H2>Stage 3: measured TDEE</H2>
      <P>
        After you've logged food for 5+ days and weighed in at least twice, we switch from the estimate to a measured TDEE (Total Daily Energy Expenditure). This is the part most apps don't do, and it's why your real maintenance can be 400 cal off from any formula's prediction.
      </P>
      <Note>
        <Strong>Measured TDEE = average daily intake − (weekly weight change × 3,500 / 7)</Strong>
      </Note>
      <P>
        If you ate 2,500 cal/day on average and lost 1 lb that week, your real TDEE is about 3,000 cal/day (you burned 500/day more than you ate). If you ate 2,500 and stayed the same weight, your maintenance is 2,500 — much lower than the 2,700 estimate. We then re-apply your goal adjustment to the measured number, so your target gets more accurate over time.
      </P>

      <H2>Confidence levels</H2>
      <P>
        Your TDEE estimate has a confidence rating you'll see on the Nutrition page:
      </P>
      <UL>
        <LI><Strong>Low:</Strong> &lt;5 days logged or &lt;2 weigh-ins. Still on the formula estimate.</LI>
        <LI><Strong>Medium:</Strong> 5+ days, 2+ weigh-ins. First real measurement.</LI>
        <LI><Strong>High:</Strong> 10+ days, 3+ weigh-ins. Confident enough to make weekly adjustments.</LI>
      </UL>

      <H2>Why we don't use Mifflin-St Jeor</H2>
      <P>
        The standard formula (Mifflin-St Jeor) uses height, age, sex, and body weight to predict basal metabolic rate, then multiplies by an activity factor. It's the most accurate formula on paper, but the predictions still vary ±15% from real measurements, and the activity multipliers are just educated guesses about how much you move.
      </P>
      <P>
        We use a simpler weight-times-multiplier as the starting point because once you've logged for a week, the measured TDEE replaces the estimate anyway. A more elaborate formula upfront gives you a slightly better starting guess but no advantage long-term.
      </P>

      <H2>What to do with this</H2>
      <UL>
        <LI>Log food consistently for the first two weeks — that's what unlocks the adaptive system.</LI>
        <LI>Weigh yourself 3–4 mornings a week. We use the average, not single days (single weigh-ins swing ±3 lbs from water).</LI>
        <LI>Don't manually override your target chasing a faster result. Bigger deficits don't mean faster fat loss; they mean more muscle loss and a higher chance of bingeing.</LI>
        <LI>Trust the trend over the day. If your weekly average is moving in the right direction, the math is working.</LI>
      </UL>
    </ArticleLayout>
  );
}
